import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button, Select, Input } from 'common/components';
import { buyPhoneNumbers, syncTrustProducts } from 'services/api/PhoneNumberAPI';
import { Tenant } from 'types/accessManager.type';
import styles from '../PhoneNumberManager.module.scss';

interface PhoneNumberForm {
    tenantId: string;
    workflowId?: string;
    areaCode: string;
    count: string;
}

interface BuyNumbersTabProps {
    tenants: Tenant[];
    isAccessManagerLoading: boolean;
    showNotification: (message: string, type: 'success' | 'error') => void;
}

const BuyNumbersTab: React.FC<BuyNumbersTabProps> = ({ tenants, isAccessManagerLoading, showNotification }) => {
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        reset,
        watch,
    } = useForm<PhoneNumberForm>({
        defaultValues: {
            tenantId: '',
            workflowId: '',
            areaCode: '',
            count: '1',
        },
    });

    const selectedTenantId = watch('tenantId');

    // Get workflows for selected tenant
    const selectedTenant = tenants.find((tenant) => tenant.id === selectedTenantId);
    const workflowOptions = selectedTenant?.workflows || [];

    const onSubmit = async (data: PhoneNumberForm) => {
        setIsSubmitting(true);

        try {
            const requestData = {
                tenantId: data.tenantId,
                areaCode: parseInt(data.areaCode, 10),
                count: parseInt(data.count, 10),
                ...(data.workflowId ? { workflowId: data.workflowId } : {}),
            };

            await buyPhoneNumbers(requestData);

            showNotification(`Successfully purchased ${data.count} phone number(s)!`, 'success');
            reset();
        } catch (error: any) {
            console.error('Error buying phone numbers:', error);
            const errorMessage =
                error.response?.data?.message || error.message || 'Failed to purchase phone numbers. Please try again.';
            showNotification(errorMessage, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSyncTrustProducts = async () => {
        if (!selectedTenantId) return;

        setIsSubmitting(true);

        try {
            await syncTrustProducts({ tenantId: selectedTenantId });
            showNotification('Successfully synced trust products!', 'success');
        } catch (error: any) {
            console.error('Error syncing trust products:', error);
            const errorMessage =
                error.response?.data?.message || error.message || 'Failed to sync trust products. Please try again.';
            showNotification(errorMessage, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Generate count options 1-100
    const countOptions = Array.from({ length: 100 }, (_, i) => ({
        value: String(i + 1),
        label: String(i + 1),
    }));

    return (
        <div className={styles.formContainer}>
            <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
                <div className={styles.formSection}>
                    <h2 className={styles.sectionTitle}>Tenant Selection</h2>

                    <div className={styles.formGroup}>
                        <Controller
                            name='tenantId'
                            control={control}
                            rules={{ required: 'Tenant is required' }}
                            render={({ field }) => (
                                <Select
                                    label='Tenant *'
                                    options={[
                                        { value: '', label: 'Select a Tenant', disabled: true },
                                        ...(tenants || []).map((tenant) => ({
                                            value: tenant.id,
                                            label: tenant.name,
                                        })),
                                    ]}
                                    {...field}
                                    disabled={isAccessManagerLoading}
                                />
                            )}
                        />
                        {errors.tenantId && <span className={styles.errorText}>{errors.tenantId.message}</span>}
                    </div>

                    <div className={styles.formGroup}>
                        <Controller
                            name='workflowId'
                            control={control}
                            render={({ field }) => (
                                <Select
                                    label='Workflow'
                                    options={[
                                        { value: '', label: 'Select a Workflow', disabled: true },
                                        ...workflowOptions.map((workflow) => ({
                                            value: workflow.id,
                                            label: workflow.title,
                                        })),
                                    ]}
                                    {...field}
                                    disabled={!selectedTenantId || isAccessManagerLoading}
                                />
                            )}
                        />
                        {errors.workflowId && <span className={styles.errorText}>{errors.workflowId.message}</span>}
                    </div>
                </div>

                <div className={styles.formSection}>
                    <h2 className={styles.sectionTitle}>Phone Number Configuration</h2>

                    <div className={styles.formGroup}>
                        <Input
                            label='Area Code *'
                            type='text'
                            {...register('areaCode', {
                                required: 'Area code is required',
                                pattern: {
                                    value: /^[0-9]{3}$/,
                                    message: 'Area code must be exactly 3 digits',
                                },
                                validate: (value) => {
                                    const num = parseInt(value, 10);
                                    return (num >= 100 && num <= 999) || 'Area code must be between 100 and 999';
                                },
                            })}
                            error={!!errors.areaCode}
                            errorText={errors.areaCode?.message}
                            placeholder='e.g., 415'
                            maxLength={3}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <Controller
                            name='count'
                            control={control}
                            rules={{ required: 'Count is required' }}
                            render={({ field }) => (
                                <Select
                                    label='Number of Phone Numbers *'
                                    options={[{ value: '', label: 'Select count', disabled: true }, ...countOptions]}
                                    {...field}
                                />
                            )}
                        />
                        {errors.count && <span className={styles.errorText}>{errors.count.message}</span>}
                    </div>
                </div>

                <div className={styles.buttonContainer}>
                    <Button type='submit' variant='primary' disabled={isSubmitting || isAccessManagerLoading}>
                        {isSubmitting ? 'Purchasing...' : 'Purchase Phone Numbers'}
                    </Button>
                    <Button
                        type='button'
                        variant='secondary'
                        disabled={!selectedTenantId || isSubmitting || isAccessManagerLoading}
                        onClick={handleSyncTrustProducts}
                    >
                        Sync Trust Products
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default BuyNumbersTab;
